import React, { useEffect } from 'react';
import {
    Box, Typography, CircularProgress, useTheme, alpha, Avatar
} from '@mui/material';
import {
    Timeline, TimelineItem, TimelineSeparator, TimelineConnector,
    TimelineContent, TimelineDot, TimelineOppositeContent
} from '@mui/lab';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchEntityTimeline } from '../../store/auditLogStore/auditLogThunk';
import { clearTimeline } from '../../store/auditLogStore/auditLogSlice';
import { ApiStatus } from '../../utils/api.types';
import { IAuditLog } from '../../utils/auditLog.types';

const formatDateParts = (dateStr: string) => {
    const date = new Date(dateStr);
    const datePart = date.toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
    const timePart = date.toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
    return { datePart, timePart };
};

// Same initials fallback as the lists' CreatedByCell avatar.
const userInitials = (name?: string) =>
    name?.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';

const formatFieldName = (field: string): string => {
    if (!field) return '';
    // "items.0.unitCost" -> "item.1.unitCost" — singularize the array name and make
    // the index 1-based so it reads like "Item 1 Unit Cost" instead of "Items 0 ...".
    const withIndex = field.replace(/([a-zA-Z]+)\.(\d+)\./g, (_m, arrName, idx) => {
        const singular = arrName.endsWith('s') && !arrName.endsWith('ss') ? arrName.slice(0, -1) : arrName;
        return `${singular}.${Number(idx) + 1}.`;
    });
    const withSpaces = withIndex.replace(/([A-Z])/g, ' $1').replace(/[_.]/g, ' ').replace(/\s+/g, ' ').trim();
    // Capitalize every word, not just the first character of the whole string.
    return withSpaces.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const SNAPSHOT_PRIORITY_FIELDS = [
    'name', 'empId', 'displayName', 'legalName', 'partyName', 'employeeName',
    'skuName', 'skuCode', 'productName', 'productCode', 'itemName', 'itemCode',
    'itemType', 'category', 'subCategory', 'hsnCode',
    'invoiceNumber', 'referenceId', 'txnId', 'entityNumber',
    'amount', 'totalAmount', 'netAmount', 'rate', 'quantity', 'qty',
    'mrp', 'sale', 'purchase', 'discount', 'baseUnitName',
    'isStockTracked', 'isBatchTracked',
    'status', 'paymentStatus', 'invoiceStatus',
    'designation', 'department', 'email', 'phone', 'mobile',
    'brand', 'color', 'description',
    'effectiveFrom', 'date', 'dateOfJoining', 'doj',
    'gstin', 'pan', 'aadhar'
];

// "grnNumberInt" / "reqNumberInt" / "transferNumberInt" are internal sort counters
// (not the human document number, which is the separate "...Number" field) — never
// show them.
const SNAPSHOT_SKIP_PATTERN = /^(_id|__v|account|createdAt|updatedAt|isDelete|isActive|password|token|otp|metadata)$|numberint$/i;

const extractSnapshotFields = (snapshot: any): Array<[string, any]> => {
    if (!snapshot || typeof snapshot !== 'object') return [];
    const entries: Array<[string, any]> = [];
    for (const key of SNAPSHOT_PRIORITY_FIELDS) {
        if (snapshot[key] !== undefined && snapshot[key] !== null && snapshot[key] !== '') {
            entries.push([key, snapshot[key]]);
        }
    }
    if (entries.length < 6) {
        for (const key of Object.keys(snapshot)) {
            if (entries.find(([k]) => k === key)) continue;
            if (SNAPSHOT_SKIP_PATTERN.test(key)) continue;
            if (NOISE_FIELD_PATTERN.test(key)) continue;
            const val = snapshot[key];
            if (val === null || val === undefined || val === '' || val === false) continue;
            if (typeof val === 'object' && !val._id && !val.$numberDecimal && !Array.isArray(val)) continue;
            entries.push([key, val]);
            if (entries.length >= 10) break;
        }
    }
    // Drop rows whose display value would be just "—" (empty arrays like
    // parentSkus/photos, id-only refs, etc.) — an empty row tells the user nothing.
    // Non-empty `items` arrays survive: they render via the dedicated per-line list.
    return entries
        .filter(([key, val]) => (key === 'items' && Array.isArray(val) && val.length > 0) || formatValue(val, key) !== '—')
        .slice(0, 14);
};

// "dispatched" -> "Dispatched", "partially_fulfilled" -> "Partially Fulfilled"
const titleCase = (s: string): string =>
    s.replace(/_/g, ' ').split(' ').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const formatValue = (value: any, field?: string): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string') {
        if (/^[a-f0-9]{24}$/i.test(value)) return '—';
        // JWT (three base64url segments separated by dots)
        if (/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)) return '—';
        // S3 / http(s) URLs and storage keys
        if (/^https?:\/\//i.test(value)) return '—';
        // ETags (quoted hex) and very long opaque tokens/base64 blobs
        if (/^"?[a-f0-9]{32}"?$/i.test(value)) return '—';
        if (value.length > 120 && /^[A-Za-z0-9+/=_-]+$/.test(value)) return '—';
        // Any recognizable date -> clean "20 Aug 2026". Covers ISO datetime
        // ("2026-08-20T…"), date-only ("2026-08-20"), and JS Date.toString()
        // ("Thu Aug 20 2026 …"). Don't misfire on plain numbers/text.
        const looksLikeDate = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}|$)/.test(value)
            || /^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}/.test(value);
        if (looksLikeDate) {
            const d = new Date(value);
            if (!isNaN(d.getTime())) return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        // Status-like enum values come from the DB lowercase ("draft", "partially_fulfilled") —
        // title-case them for display so they read like the rest of the panel.
        if (field && /status/i.test(field) && /^[a-z]+(_[a-z]+)*$/.test(value)) {
            return titleCase(value);
        }
        return value;
    }
    if (typeof value === 'object') {
        if (value.$numberDecimal !== undefined) return String(value.$numberDecimal);
        if (Array.isArray(value)) {
            if (!value.length) return '—';
            const parts = value.slice(0, 3).map(v => formatValue(v, field)).filter(v => v && v !== '—');
            return parts.length ? parts.join(', ') + (value.length > 3 ? ` +${value.length - 3} more` : '') : '—';
        }
        // SKU price sub-objects ({withTax, price, unit}) — show "₹180 / PCS (without tax)"
        // instead of dropping to "—" because the object has no `name` of its own.
        if (value.price !== undefined || value.withTax !== undefined) {
            if (value.price === undefined || value.price === null) return '—';
            const unitName = value.unit && typeof value.unit === 'object' ? (value.unit.code || value.unit.name || '') : '';
            const tax = value.withTax === true ? ' (with tax)' : value.withTax === false ? ' (without tax)' : '';
            return `₹${value.price}${unitName ? ` / ${unitName}` : ''}${tax}`;
        }
        // SKU discount sub-object — "10% / ₹20 / above qty 5"
        if (value.discountPercent !== undefined || value.discountAmount !== undefined || value.discountAboveQty !== undefined) {
            const bits: string[] = [];
            if (value.discountPercent) bits.push(`${value.discountPercent}%`);
            if (value.discountAmount) bits.push(`₹${value.discountAmount}`);
            if (value.discountAboveQty) bits.push(`above qty ${value.discountAboveQty}`);
            return bits.length ? bits.join(' / ') : '—';
        }
        const name = value.name || value.displayName || value.legalName || value.headName || value.skuName || value.empId || value.label;
        if (name) return String(name);
        if (value._id) return '—';
        return '—';
    }
    return String(value);
};

// GRN/Requisition/Transfer line items don't have a "name" of their own (just a
// skuId ref + qty/batch/price fields), so the generic formatValue object-name
// lookup falls through to "—" for every line. Build a readable "SKU — Qty, Batch,
// MRP..." summary instead, covering every field shape (qtyReceived on GRN lines,
// qtyRequested/qtyApproved on Requisition lines, qty on Transfer lines).
const getItemLineParts = (item: any): { name: string; parts: string[] } => {
    if (!item || typeof item !== 'object') return { name: '', parts: [] };
    const name = item.skuId?.name || item.skuName || item.itemCode || 'Item';
    const parts: string[] = [];
    if (item.qtyReceived !== undefined) parts.push(`Qty: ${item.qtyReceived}`);
    else if (item.qtyApproved !== undefined || item.qtyRequested !== undefined) {
        parts.push(`Requested: ${item.qtyRequested ?? 0}`);
        if (item.qtyApproved !== undefined) parts.push(`Approved: ${item.qtyApproved}`);
    } else if (item.qty !== undefined) parts.push(`Qty: ${item.qty}`);
    if (item.batchNumber) parts.push(`Batch: ${item.batchNumber}`);
    if (item.mfgDate) parts.push(`Mfg: ${formatValue(item.mfgDate)}`);
    if (item.expiryDate) parts.push(`Exp: ${formatValue(item.expiryDate)}`);
    if (item.mrp) parts.push(`MRP: ₹${item.mrp}`);
    if (item.purchasePrice) parts.push(`Purchase: ₹${item.purchasePrice}`);
    if (item.salePrice) parts.push(`Sale: ₹${item.salePrice}`);
    return { name, parts };
};

const COLLAPSED_FIELD_PATTERN = /(^|\.)(attachments?|files?|photo|aadhar|tinPan|passbook|pvc|bvc|signature|thumbPhoto|bgvConsentPhoto|termsAndConditionPhoto|documents?|metadata|snapshot|salt|hash|barcode|barcodeImage)(\.|$)/i;

// Internal/system fields the end user should never see in an audit trail:
// JWT tokens, S3/PDF locations, e-tags, storage keys, raw jwt/base64 blobs, etc.
// NOTE: matched against field labels too (e.g. "House Wishkey removed"), so short
// words like "key"/"url"/"location" MUST be word-bounded — otherwise an item named
// "Wishkey" (contains "key") gets wrongly hidden.
const NOISE_FIELD_PATTERN = /(\btoken\b|dispatchtoken|\bjwt\b|\betag\b|e_tag|pdfurl|pdf_url|pdflocation|signedurl|presigned|\bchecksum\b|\bmimetype\b|\bfilename\b|\bfilepath\b|\bcontenttype\b|\burl\b|\blocation\b|\bkey\b)/i;

const stripIds = (text: string): string => {
    if (!text) return text;
    let cleaned = text;
    cleaned = cleaned.replace(/\b[a-f0-9]{24}\b/gi, '');
    cleaned = cleaned.replace(/\(\s*\)/g, '');
    cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
    return cleaned;
};

const buildActionTitle = (log: IAuditLog): string => {
    const action = (log.action || '').toLowerCase();
    const entity = log.entity || 'record';
    const fallback =
        action === 'create' ? `${entity} updated`.replace('updated', 'created')
        : action === 'delete' ? `${entity} deleted`
        : action && action !== 'update' ? `${entity} ${action}`
        : `${entity} updated`;

    if (log.description) {
        // Drop meaningless "status changed from X to X" descriptions (no real change).
        const noOp = log.description.match(/status changed from (.+?) to (.+?)(\s*$|\s+—)/i);
        if (noOp && noOp[1].trim() === noOp[2].trim()) return fallback;
        return stripIds(log.description);
    }
    return fallback;
};

// Compute the changes that will actually render for a log, and whether the whole
// log should be shown at all. Shared so we can both render and detect the
// "everything got suppressed" case (to still show the empty-state message).
const computeVisible = (log: IAuditLog): { cleanChanges: IAuditLog['changes']; visible: boolean } => {
    const isBusinessAction = !['CREATE', 'UPDATE', 'DELETE'].includes(log.action);
    let cleanChanges = Array.isArray(log.changes)
        ? log.changes.filter(c => {
            if (!c.field) return false;
            if (COLLAPSED_FIELD_PATTERN.test(c.field)) return false;
            if (NOISE_FIELD_PATTERN.test(c.field)) return false;
            const ov = formatValue(c.oldValue);
            const nv = formatValue(c.newValue);
            if (ov === nv) return false;
            if (ov === '—' && nv === '—') return false;
            if (isBusinessAction && ov === '—') return false;
            return true;
        })
        : [];

    if (!isBusinessAction && cleanChanges.length > 6) {
        cleanChanges = cleanChanges.filter(c =>
            formatValue(c.oldValue) !== '—' || /\b(added|removed)\b/i.test(c.field)
        );
    }

    const hasSnapshot = !!log.snapshot && ['CREATE', 'DELETE'].includes(log.action);
    const isUpdate = log.action === 'UPDATE';
    const visible = !(isUpdate && cleanChanges.length === 0 && !hasSnapshot);
    return { cleanChanges, visible };
};

interface AuditTimelineProps {
    entity: string;
    entityId: string;
}

const AuditTimeline: React.FC<AuditTimelineProps> = ({ entity, entityId }) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const { timeline, timelineStatus } = useAppSelector(state => state.auditLog);

    // Fixed purple to match the Audit Log drawer header (#7c3aed) instead of the
    // page's primary color, which varies and clashed with the header.
    const accent = '#7c3aed';
    const accentSoft = 'rgba(124, 58, 237, 0.25)';
    const removedColor = theme.palette.error.main;
    const addedColor = theme.palette.success.main;
    const mutedColor = theme.palette.text.secondary;
    const strongColor = theme.palette.text.primary;

    useEffect(() => {
        if (entity && entityId) {
            dispatch(fetchEntityTimeline({ entity, entityId }));
        }
        return () => {
            dispatch(clearTimeline());
        };
    }, [entity, entityId, dispatch]);

    if (timelineStatus === ApiStatus.LOADING) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    // Empty, OR every log got suppressed (e.g. only no-op status-change events) —
    // show the empty state rather than a blank panel.
    const hasVisible = timeline.some(log => computeVisible(log).visible);
    if (timeline.length === 0 || !hasVisible) {
        return (
            <Box p={2}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    No activity recorded yet
                </Typography>
            </Box>
        );
    }

    return (
        <Timeline
            sx={{
                // Drawer body gives 16px; +8px here puts the date text at 24px,
                // flush with the header icon (which sits at px: 3).
                pl: 1,
                pr: 1,
                py: 1,
                m: 0,
                // Left (date) column: left-aligned so there's no dead space on the
                // left edge; wide enough that "03 Aug 2026" never wraps mid-date.
                '& .MuiTimelineOppositeContent-root': {
                    flex: 'none',
                    width: 96,
                    py: 1.5,
                    pl: 0,
                    pr: 1,
                    textAlign: 'left',
                },
                // Right (content) column: take the rest, keep a little right breathing space.
                '& .MuiTimelineContent-root': { py: 1, pl: 1.5, pr: 1 },
                '& .MuiTimelineItem-root:before': { display: 'none' },
            }}
        >
            {timeline.map((log) => {
                const { cleanChanges, visible } = computeVisible(log);
                if (!visible) return null;
                const userName = log.user?.name || log.userName || 'System';
                const { datePart, timePart } = formatDateParts(log.createdAt);
                const title = buildActionTitle(log);

                return (
                    <TimelineItem key={log._id}>
                        <TimelineOppositeContent>
                            <Typography sx={{ fontSize: '0.8rem', color: mutedColor, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                {datePart}
                            </Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: mutedColor, whiteSpace: 'nowrap' }}>
                                {timePart}
                            </Typography>
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                            <TimelineDot sx={{ bgcolor: accent, boxShadow: 'none', my: 1.25, width: 10, height: 10, p: 0 }} />
                            <TimelineConnector sx={{ bgcolor: accentSoft }} />
                        </TimelineSeparator>
                        <TimelineContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Avatar src={log.user?.profilePic || ''} sx={{ width: 22, height: 22, fontSize: '0.6rem', bgcolor: accent }}>
                                    {userInitials(userName)}
                                </Avatar>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: accent }}>
                                    {userName}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: addedColor }} />
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, color: strongColor }}>
                                    {title}
                                </Typography>
                            </Box>
                            {log.action && (
                                <Typography sx={{ fontSize: '0.75rem', color: mutedColor, fontStyle: 'italic', mt: 0.25 }}>
                                    Action: {log.action.charAt(0) + log.action.slice(1).toLowerCase()}
                                </Typography>
                            )}
                            {cleanChanges.length > 0 && (
                                <Box sx={{ mt: 0.75, p: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                                    {cleanChanges.map((c, idx) => {
                                        // "X added" / "X removed" rows: show a single badge line, not "value → —".
                                        const isAdded = /\badded\b/i.test(c.field);
                                        const isRemoved = /\bremoved\b/i.test(c.field);
                                        if (isAdded || isRemoved) {
                                            return (
                                                <Box key={`${log._id}-${c.field}-${idx}`} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5, fontSize: '0.78rem', mb: idx === cleanChanges.length - 1 ? 0 : 0.4 }}>
                                                    <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: isAdded ? addedColor : removedColor, textDecoration: isRemoved ? 'line-through' : 'none', wordBreak: 'break-word' }}>
                                                        {formatFieldName(c.field)}
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return (
                                        <Box key={`${log._id}-${c.field}-${idx}`} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5, fontSize: '0.78rem', mb: idx === cleanChanges.length - 1 ? 0 : 0.4 }}>
                                            <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: strongColor }}>
                                                {formatFieldName(c.field)}:
                                            </Typography>
                                            <Typography component="span" sx={{ fontSize: '0.78rem', color: removedColor, textDecoration: 'line-through', wordBreak: 'break-word' }}>
                                                {formatValue(c.oldValue, c.field)}
                                            </Typography>
                                            <Typography component="span" sx={{ fontSize: '0.78rem', color: mutedColor }}>→</Typography>
                                            <Typography component="span" sx={{ fontSize: '0.78rem', color: addedColor, fontWeight: 600, wordBreak: 'break-word' }}>
                                                {formatValue(c.newValue, c.field)}
                                            </Typography>
                                        </Box>
                                        );
                                    })}
                                </Box>
                            )}
                            {log.snapshot && ['CREATE', 'DELETE'].includes(log.action) && (
                                <Box sx={{ mt: 0.75, p: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                                    {extractSnapshotFields(log.snapshot).map(([key, val]) => {
                                        // Line items have no "name" of their own (just skuId + qty/batch/price),
                                        // so the generic single-row format collapses to "Items: —". List each
                                        // line instead so what was actually entered is visible.
                                        if (key === 'items' && Array.isArray(val) && val.length > 0) {
                                            return (
                                                <Box key={`${log._id}-snap-${key}`} sx={{ mb: 0.4 }}>
                                                    <Typography component="div" sx={{ fontSize: '0.78rem', fontWeight: 600, color: strongColor }}>
                                                        {formatFieldName(key)} ({val.length}):
                                                    </Typography>
                                                    {val.slice(0, 8).map((item: any, i: number) => {
                                                        const { name, parts } = getItemLineParts(item);
                                                        // Two fields per row keeps the panel compact without one long line.
                                                        const rows: string[][] = [];
                                                        for (let pi = 0; pi < parts.length; pi += 2) rows.push(parts.slice(pi, pi + 2));
                                                        return (
                                                            <Box key={`${log._id}-snap-item-${i}`} sx={{ pl: 1, mb: 1.25 }}>
                                                                <Typography component="div" sx={{ fontSize: '0.76rem', color: log.action === 'CREATE' ? addedColor : removedColor, fontWeight: 600, wordBreak: 'break-word' }}>
                                                                    • {name}
                                                                </Typography>
                                                                {rows.map((pair, ri) => (
                                                                    <Box key={`${log._id}-snap-item-${i}-${ri}`} sx={{ display: 'flex', gap: 1, pl: 1.5 }}>
                                                                        {pair.map((p, pi) => (
                                                                            <Typography key={`${log._id}-snap-item-${i}-${ri}-${pi}`} component="div" sx={{ fontSize: '0.74rem', color: log.action === 'CREATE' ? addedColor : removedColor, wordBreak: 'break-word', flex: 1, minWidth: 0 }}>
                                                                                {p}
                                                                            </Typography>
                                                                        ))}
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        );
                                                    })}
                                                    {val.length > 8 && (
                                                        <Typography component="div" sx={{ fontSize: '0.76rem', color: mutedColor, pl: 1 }}>
                                                            +{val.length - 8} more
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        }
                                        return (
                                            <Box key={`${log._id}-snap-${key}`} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.5, fontSize: '0.78rem', mb: 0.4 }}>
                                                <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: strongColor }}>
                                                    {formatFieldName(key)}:
                                                </Typography>
                                                <Typography component="span" sx={{ fontSize: '0.78rem', color: log.action === 'CREATE' ? addedColor : removedColor, fontWeight: 500, wordBreak: 'break-word' }}>
                                                    {formatValue(val, key)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </TimelineContent>
                    </TimelineItem>
                );
            })}
        </Timeline>
    );
};

export default AuditTimeline;
